import { Box, Button, Typography, Stack, Fade } from "@mui/material";
import { useAppSelector } from "hooks/use-app-select";
import { isTruthy } from "helpers/methods";
import { RequestFile } from "models/interfaces";
import React, { useEffect, useState } from "react";
import { focusRing } from "theme/colors";
import { setReduxSelectedFile } from "redux/appSlice";
import { useAppDispatch } from "redux/hooks";
import editorTokens from "../SignRequestEditor.tokens";
import { DocumentsTabIcon, FileIcon } from "./EditorIcons";
import RightPanelHeader from "./RightPanelHeader";
import signerTokens from "../ParticipantScreen/SignerExperience.tokens";
import { FileIcon as SignerFileIcon } from "../ParticipantScreen/components/SignerExperienceIcons";

const { documents, palette, rightRail, scrollbar } = editorTokens;

/** "Priya Sharma" → "PS". Used by the per-recipient field-count chips. */
const getInitials = (value?: string) =>
  (value || "?")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0])
    .join("")
    .toUpperCase();

/**
 * A "Required 2/3" style stat with a leading status dot. Used on each signer
 * document card to break the completion count into required vs optional fields.
 */
const FieldCountStat = ({
  label,
  done,
  total,
  dotColor,
}: {
  label: string;
  done: number;
  total: number;
  dotColor: string;
}) => (
  <Box
    sx={{ display: "inline-flex", alignItems: "center", gap: "5px", minWidth: 0 }}
  >
    <Box
      component="span"
      sx={{
        width: 6,
        height: 6,
        borderRadius: "50%",
        backgroundColor: dotColor,
        flexShrink: 0,
      }}
    />
    <Typography
      sx={{
        fontSize: signerTokens.panel.progressFontSize,
        color: signerTokens.palette.meta,
        lineHeight: "normal",
      }}
    >
      {label}
    </Typography>
    <Typography
      sx={{
        fontSize: signerTokens.panel.progressFontSize,
        fontWeight: 700,
        color: signerTokens.palette.ink,
        lineHeight: "normal",
        whiteSpace: "nowrap",
      }}
    >
      {done}/{total}
    </Typography>
  </Box>
);

interface DocumentsProps {
  docLoading: boolean;
  onFileChange?: (file: RequestFile) => void;
  hideHeader?: boolean;
  headerAction?: React.ReactNode;
  /**
   * `signer` (default) shows each document's completion progress for the
   * recipient. `editor` shows the design's per-recipient placed-field summary.
   */
  variant?: "signer" | "editor";
  /** Editor variant only: jumps back to the upload step. */
  onAddDocuments?: () => void;
}

const Documents = ({
  docLoading,
  onFileChange,
  hideHeader,
  headerAction,
  variant = "signer",
  onAddDocuments,
}: DocumentsProps) => {
  const dispatch = useAppDispatch();
  const { signRequest, selectedFile, selectedParticipant } = useAppSelector(
    (state) => state.app,
  );
  const signerIdx =
    signRequest?.signers?.findIndex(
      (s: any) => s.email === selectedParticipant?.email,
    ) ?? -1;
  const normalizedSelected: any = selectedFile && (selectedFile as any);
  const file =
    normalizedSelected?.unsignedDoc || normalizedSelected?.unsignedPdf
      ? normalizedSelected
      : signRequest.files[0];

  const getFileName = (file: RequestFile | null | undefined) => {
    if (!file) return "";
    const fileName =
      (file.unsignedDoc || file.unsignedPdf)?.split("/")[1] ??
      (file.unsignedDoc || file.unsignedPdf)?.split("/")[0] ??
      "";
    return fileName;
  };

  const [value, setValue] = useState(() => getFileName(file));

  // Sync value state with Redux selectedFile
  useEffect(() => {
    if (selectedFile) {
      const fileName = getFileName(selectedFile);
      if (fileName) {
        setValue(fileName);
      }
    } else if (signRequest?.files?.[0]) {
      const fileName = getFileName(signRequest.files[0]);
      if (fileName) {
        setValue(fileName);
      }
    }
  }, [
    selectedFile?.unsignedDoc,
    selectedFile?.unsignedPdf,
    signRequest?.files,
  ]);

  useEffect(() => {
    if (!file) return;
    const normalized: any = {
      ...file,
      unsignedDoc:
        (file as any).unsignedDoc || (file as any)?.unsignedPdf || "",
      unsignedDocUrl:
        (file as any).unsignedDocUrl || (file as any)?.unsignedPdfUrl || "",
    };
    dispatch(setReduxSelectedFile(normalized));
  }, [file?.unsignedDoc, file?.unsignedPdf]);

  const handleDocumentClick = (
    file: RequestFile,
    option: { label: string; val: string },
  ) => {
    if (option.val !== value) {
      setValue(option.val);
      const normalizedClick: any = {
        ...file,
        unsignedDoc:
          (file as any).unsignedDoc || (file as any).unsignedPdf || "",
        unsignedDocUrl:
          (file as any).unsignedDocUrl || (file as any).unsignedPdfUrl || "",
      };
      dispatch(setReduxSelectedFile(normalizedClick));
      // Call onFileChange callback if provided (for ParticipantScreen)
      if (onFileChange) {
        onFileChange(normalizedClick);
      }
    }
  };

  if (variant === "editor") {
    const files: RequestFile[] = signRequest?.files || [];
    const totalPages = files.reduce(
      (sum: number, doc: RequestFile) => sum + (doc.pageCount || 0),
      0,
    );

    return (
      <Fade in={true} timeout={500}>
        <Stack sx={{ height: "100%", width: "100%", overflow: "hidden" }}>
          {!hideHeader && (
            <RightPanelHeader
              icon={<DocumentsTabIcon size={rightRail.headerIconSize} />}
              title="Documents"
              headerAction={headerAction}
            />
          )}
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              overflowY: "auto",
              p: documents.padding,
              "&::-webkit-scrollbar": { width: scrollbar.size },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: scrollbar.thumb,
                border: scrollbar.thumbBorder,
                backgroundClip: "content-box",
                borderRadius: scrollbar.radius,
              },
              "&::-webkit-scrollbar-track": { background: "transparent" },
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: documents.metaMarginBottom,
                gap: 1,
              }}
            >
              <Typography
                sx={{
                  fontSize: documents.metaFontSize,
                  fontWeight: documents.metaFontWeight,
                  color: palette.muted,
                  lineHeight: "normal",
                }}
              >
                {files.length} document{files.length === 1 ? "" : "s"}
                {totalPages > 0
                  ? ` · ${totalPages} page${totalPages === 1 ? "" : "s"}`
                  : ""}
              </Typography>
              {onAddDocuments && (
                <Button
                  onClick={onAddDocuments}
                  sx={{
                    "&.MuiButton-root": {
                      minWidth: "auto",
                      p: documents.addPadding,
                      borderRadius: documents.addRadius,
                      background: palette.accentTint,
                      color: palette.accent,
                      fontSize: documents.addFontSize,
                      fontWeight: documents.addFontWeight,
                      textTransform: "none",
                      letterSpacing: "normal",
                      lineHeight: "normal",
                      "&:hover": { background: palette.accentBorder },
                    },
                  }}
                >
                  + Add
                </Button>
              )}
            </Box>

            {files.map((doc: RequestFile, index: number) => {
              const fileName = getFileName(doc);
              const isSelected = fileName === value;
              const placed = (doc.fields || []).filter((f: any) => !f.hidden);
              const perSigner = (signRequest?.signers || [])
                .map((signer: any) => ({
                  signer,
                  count: placed.filter(
                    (f: any) => f.signerIndex === signer.id,
                  ).length,
                }))
                .filter((entry: any) => entry.count > 0);

              return (
                <Box
                  key={index}
                  role="button"
                  tabIndex={0}
                  aria-pressed={isSelected}
                  onClick={() =>
                    handleDocumentClick(doc, {
                      label: fileName,
                      val: fileName,
                    })
                  }
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      handleDocumentClick(doc, {
                        label: fileName,
                        val: fileName,
                      });
                    }
                  }}
                  sx={{
                    cursor: isSelected ? "default" : "pointer",
                    display: "flex",
                    flexDirection: "column",
                    gap: documents.cardGap,
                    p: documents.cardPadding,
                    mb: documents.cardMarginBottom,
                    borderRadius: documents.cardRadius,
                    border: `1px solid ${
                      isSelected ? palette.accent : palette.borderLight
                    }`,
                    backgroundColor: isSelected
                      ? palette.accentTint
                      : palette.white,
                    transition: "border-color .15s ease",
                    "&:hover": { borderColor: palette.accent },
                    "&:focus-visible": {
                      outline: `2px solid ${focusRing}`,
                      outlineOffset: "2px",
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: documents.cardGap,
                      minWidth: 0,
                    }}
                  >
                    <Box
                      component="span"
                      sx={{
                        display: "flex",
                        flexShrink: 0,
                        color: isSelected ? palette.accent : palette.muted,
                      }}
                    >
                      <FileIcon size={documents.cardIconSize} />
                    </Box>
                    <Typography
                      sx={{
                        flex: 1,
                        minWidth: 0,
                        fontSize: documents.cardNameFontSize,
                        fontWeight: documents.cardNameFontWeight,
                        color: palette.ink,
                        lineHeight: "normal",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {fileName}
                    </Typography>
                    {!!doc.pageCount && (
                      <Typography
                        sx={{
                          flexShrink: 0,
                          fontSize: documents.cardMetaFontSize,
                          color: palette.muted,
                          lineHeight: "normal",
                        }}
                      >
                        {doc.pageCount} page{doc.pageCount === 1 ? "" : "s"}
                      </Typography>
                    )}
                  </Box>

                  {placed.length > 0 ? (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: documents.chipGap,
                        flexWrap: "wrap",
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: documents.countFontSize,
                          fontWeight: documents.countFontWeight,
                          color: palette.ink,
                          lineHeight: "normal",
                        }}
                      >
                        {placed.length} field{placed.length === 1 ? "" : "s"}{" "}
                        placed
                      </Typography>
                      {perSigner.map((entry: any) => (
                        <Box
                          key={entry.signer.id}
                          sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "5px",
                            fontSize: documents.chipFontSize,
                            fontWeight: documents.chipFontWeight,
                            color: palette.body,
                            backgroundColor: palette.white,
                            border: `1px solid ${palette.borderLight}`,
                            borderRadius: documents.chipRadius,
                            p: documents.chipPadding,
                          }}
                        >
                          <Box
                            component="span"
                            sx={{
                              width: documents.chipAvatarSize,
                              height: documents.chipAvatarSize,
                              borderRadius: "50%",
                              backgroundColor:
                                entry.signer.color || palette.ctaBg,
                              color: palette.white,
                              fontSize: documents.chipAvatarFontSize,
                              fontWeight: 700,
                              display: "inline-flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            {getInitials(
                              entry.signer.name ||
                                entry.signer.requestRole ||
                                entry.signer.email,
                            )}
                          </Box>
                          {entry.count}
                        </Box>
                      ))}
                    </Box>
                  ) : (
                    <Typography
                      sx={{
                        fontSize: documents.countFontSize,
                        fontWeight: 600,
                        color: palette.muted,
                        lineHeight: "normal",
                      }}
                    >
                      No fields placed yet
                    </Typography>
                  )}
                </Box>
              );
            })}

            <Typography
              sx={{
                mt: documents.hintMarginTop,
                fontSize: documents.hintFontSize,
                lineHeight: documents.hintLineHeight,
                color: palette.muted,
              }}
            >
              Fields you place here are what each recipient will see when they
              open the request.
            </Typography>
          </Box>
        </Stack>
      </Fade>
    );
  }

  return (
    <Fade in={true} timeout={500}>
      <Stack
        direction="column"
        display="flex"
        justifyContent="space-between"
        sx={{
          height: "100%",
          width: "100%",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {!hideHeader && (
            <Box
              sx={{
                p: signerTokens.panel.headerPadding,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 1,
                flexShrink: 0,
                borderBottom: `1px solid ${signerTokens.palette.borderLight}`,
              }}
            >
              <Typography
                sx={{
                  fontSize: signerTokens.panel.headerTitleFontSize,
                  fontWeight: signerTokens.panel.headerTitleFontWeight,
                  color: signerTokens.palette.ink,
                  minWidth: 0,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                Documents
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  flexShrink: 0,
                }}
              >
                <Typography
                  sx={{
                    fontSize: signerTokens.panel.headerMetaFontSize,
                    fontWeight: signerTokens.panel.headerMetaFontWeight,
                    color: signerTokens.palette.meta,
                    whiteSpace: "nowrap",
                    lineHeight: 1,
                  }}
                >
                  {signRequest?.files?.length || 0} files
                </Typography>
                {headerAction}
              </Box>
            </Box>
          )}

          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              overflowY: "auto",
              overflowX: "hidden",
              backgroundColor: signerTokens.palette.white,
              display: "flex",
              flexDirection: "column",
              gap: signerTokens.panel.listGap,
              p: signerTokens.panel.listPadding,
              boxSizing: "border-box",
              "&::-webkit-scrollbar": {
                width: signerTokens.scrollbar.size,
              },
              "&::-webkit-scrollbar-track": {
                backgroundColor: "transparent",
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: signerTokens.scrollbar.thumb,
                borderRadius: signerTokens.scrollbar.radius,
                "&:hover": {
                  backgroundColor: signerTokens.scrollbar.thumbHover,
                },
              },
            }}
          >
            {signRequest?.files?.map((file: RequestFile, index: number) => {
              const fileName = getFileName(file);
              const option = {
                label: fileName,
                val: fileName,
              };
              const isSelected = option.val === value;
              const myFields = (file?.fields || []).filter(
                (f: any) => f.signerIndex === signerIdx && !f.hidden,
              );
              const hasFields = myFields.length > 0;
              const completed = myFields.filter((f: any) =>
                isTruthy(f.value),
              ).length;
              const pct = hasFields
                ? Math.round((completed / myFields.length) * 100)
                : 0;
              const requiredFields = myFields.filter((f: any) => f.required);
              const optionalFields = myFields.filter((f: any) => !f.required);
              const requiredCompleted = requiredFields.filter((f: any) =>
                isTruthy(f.value),
              ).length;
              const optionalCompleted = optionalFields.filter((f: any) =>
                isTruthy(f.value),
              ).length;

              return (
                <Box
                  key={index}
                  role="button"
                  tabIndex={0}
                  aria-pressed={isSelected}
                  onClick={() => handleDocumentClick(file, option)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      handleDocumentClick(file, option);
                    }
                  }}
                  sx={{
                    cursor: isSelected ? "default" : "pointer",
                    boxSizing: "border-box",
                    maxWidth: "100%",
                    minWidth: 0,
                    flexShrink: 0,
                    display: "flex",
                    flexDirection: "column",
                    gap: signerTokens.panel.cardGap,
                    p: signerTokens.panel.cardPadding,
                    borderRadius: signerTokens.panel.cardRadius,
                    border: `1px solid ${
                      isSelected
                        ? signerTokens.palette.accentBorder
                        : signerTokens.palette.border
                    }`,
                    backgroundColor: isSelected
                      ? signerTokens.palette.accentTint
                      : signerTokens.palette.white,
                    transition: "border-color 0.15s ease",
                    "&:hover": {
                      borderColor: signerTokens.palette.accent,
                    },
                    "&:focus-visible": {
                      outline: `2px solid ${focusRing}`,
                      outlineOffset: "2px",
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: signerTokens.panel.cardGap,
                      minWidth: 0,
                    }}
                  >
                    <Box
                      component="span"
                      sx={{
                        display: "flex",
                        flexShrink: 0,
                        color: isSelected
                          ? signerTokens.palette.accent
                          : signerTokens.palette.muted,
                      }}
                    >
                      <SignerFileIcon size={signerTokens.panel.cardIconSize} />
                    </Box>
                    <Typography
                      sx={{
                        color: signerTokens.palette.ink,
                        fontWeight: signerTokens.panel.cardNameFontWeight,
                        fontSize: signerTokens.panel.cardNameFontSize,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        flex: 1,
                        minWidth: 0,
                      }}
                    >
                      {option.label}
                    </Typography>
                  </Box>

                  {hasFields ? (
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: signerTokens.panel.progressGap,
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          gap: 1,
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: signerTokens.panel.progressFontSize,
                            color: signerTokens.palette.meta,
                          }}
                        >
                          Fields completed
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: signerTokens.panel.progressFontSize,
                            fontWeight: 700,
                            color: signerTokens.palette.ink,
                            whiteSpace: "nowrap",
                            flexShrink: 0,
                          }}
                        >
                          {completed} of {myFields.length}
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          height: signerTokens.panel.progressHeight,
                          borderRadius: signerTokens.panel.progressRadius,
                          backgroundColor: signerTokens.palette.progressTrack,
                          overflow: "hidden",
                        }}
                      >
                        <Box
                          sx={{
                            height: "100%",
                            width: `${pct}%`,
                            borderRadius: signerTokens.panel.progressRadius,
                            backgroundColor: signerTokens.palette.accent,
                            transition: "width 0.2s ease",
                          }}
                        />
                      </Box>

                      {(requiredFields.length > 0 ||
                        optionalFields.length > 0) && (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            flexWrap: "wrap",
                            gap: "5px 14px",
                          }}
                        >
                          {requiredFields.length > 0 && (
                            <FieldCountStat
                              label="Required"
                              done={requiredCompleted}
                              total={requiredFields.length}
                              dotColor={signerTokens.palette.accent}
                            />
                          )}
                          {optionalFields.length > 0 && (
                            <FieldCountStat
                              label="Optional"
                              done={optionalCompleted}
                              total={optionalFields.length}
                              dotColor={signerTokens.palette.muted}
                            />
                          )}
                        </Box>
                      )}
                    </Box>
                  ) : (
                    <Typography
                      sx={{
                        fontSize: signerTokens.panel.reviewOnlyFontSize,
                        fontWeight: signerTokens.panel.reviewOnlyFontWeight,
                        color: signerTokens.palette.purple,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      Review only · no fields for you
                    </Typography>
                  )}
                </Box>
              );
            })}

            <Typography
              sx={{
                fontSize: signerTokens.panel.footnoteFontSize,
                lineHeight: signerTokens.panel.footnoteLineHeight,
                color: signerTokens.palette.muted,
              }}
            >
              Signed copies of every document are emailed to all parties when
              the request completes.
            </Typography>
          </Box>
        </Box>
      </Stack>
    </Fade>
  );
};

export default Documents;
